// CGameSetupScene — 新牌局／圈結束的底台選擇。
//
// === 使用圖片 ===
// 無直接圖片 — 桌面由 createTableBackground（Phaser.Graphics）繪製，
// 右上 X 使用 close-button（純 Graphics，詳見該檔頭）。
// 面板由 page-skin 繪製；方案與按鈕經 dialog-skin 使用綠金面板與立體按鈕，詳見各檔頭。

import Phaser from 'phaser';
import { createCloseButton, CloseButtonLayout } from '@ui/close-button';
import { animateTo, glow, leaveScene, Motion } from '@ui/motion';
import { BGMTrack } from '@audio/audio-keys';
import { playUIClick, syncBackgroundMusic } from '@audio/scene-audio';

import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '@config/game-config';
import {
  BetPlans,
  GameSetStatus,
  canAllPlayersAfford,
  createGameSet,
  prepareNextRound,
  rollDice,
  type IBetPlan,
} from '@core/index';
import { traceGameSet } from '../debug/round-trace';
import { loadGameSave, saveActiveSession, type IGameSave } from '@storage/index';
import { createTableBackground } from '@ui/table-background';
import { createPagePanel, createPageTitle, createPageButton, PAGE_THEME } from '@ui/page-skin';
import { createDialogButtonSurface, type CDialogButtonSurface } from '@ui/dialog-skin';

import { accountSnapshot, nextRound, startSet } from '@net/account-client';
import { TABLE_ECONOMY, tableFeeFor } from '@net/account-rules';
import { isServerTableEnabled } from '@net/server-mode';
import { maybeShowHelpPrompt } from '@ui/help-prompt';
import { noticeFromError } from '@ui/notice-toast';
import { SceneKey } from './scene-keys';
import { findTable, createSelectedTableProfile, TableId, type TTableId } from '@core/table-catalog';

const SELECTION_LINE = 2; // 移動選中框線寬
const SELECTION_PAD = 5; // 選中框外距
const SELECTION_RADIUS = 20; // 選中框圓角
const PANEL_X = LOGICAL_WIDTH / 2;
const PANEL_Y = LOGICAL_HEIGHT / 2;
const PANEL_WIDTH = 760;
const PANEL_HEIGHT = 600;
const TITLE_Y = 106;
const BALANCE_Y = 160;
const BALANCE_FONT_SIZE = 22;
const PLAN_START_Y = 215;
const PLAN_STEP_Y = 64;
const PLAN_WIDTH = 520;
const PLAN_HEIGHT = 50;
const PLAN_FONT_SIZE = 26;
const INFO_Y = 540;
const INFO_FONT_SIZE = 20;
const START_Y = 612;
const START_WIDTH = 260;
const START_HEIGHT = 62;
const DISABLED_ALPHA = 0.5; // 籌碼不足方案亮度
const DEFAULT_BET_INDEX = 1;
const TABLE_LABEL_Y = 568;

export interface IGameSetupData {
  readonly preview?: boolean;
  readonly tableId?: TTableId;
  readonly selectedIndex?: number;
}

export class CGameSetupScene extends Phaser.Scene {
  private save!: IGameSave;
  private selectedIndex = DEFAULT_BET_INDEX;
  private preview = false;
  private tableId: TTableId = TableId.Beginner;
  private starting = false;
  private server = false; // 伺服器牌局：開將與換圈由伺服器決定
  private planSurfaces: CDialogButtonSurface[] = [];
  private planLabels: Phaser.GameObjects.Text[] = [];
  private selectionFrame: Phaser.GameObjects.Graphics | undefined;

  constructor() {
    super({ key: SceneKey.GameSetup });
  }

  create(data: IGameSetupData = {}): void {
    this.starting = false;
    this.cameras.main.resetFX();
    this.planSurfaces = [];
    this.planLabels = [];
    this.selectionFrame = undefined;
    this.selectedIndex =
      Number.isInteger(data.selectedIndex) &&
      data.selectedIndex! >= 0 &&
      data.selectedIndex! < BetPlans.length
        ? data.selectedIndex!
        : DEFAULT_BET_INDEX;
    this.tableId = findTable(data.tableId)?.id ?? TableId.Beginner;
    this.server = isServerTableEnabled();
    syncBackgroundMusic(this, BGMTrack.Lobby);
    this.preview =
      data.preview === true ||
      new URLSearchParams(window.location.search).get('scene') === SceneKey.GameSetup;
    createTableBackground(this);

    try {
      this.save = this.preview ? this.previewSave() : loadGameSave();
    } catch (error) {
      this.renderFatalError(error);
      return;
    }

    const active = this.save.activeSession;
    if (!this.preview && active && active.status !== GameSetStatus.CircleComplete) {
      this.scene.start(SceneKey.Home);
      return;
    }
    if (!this.preview && !active && !findTable(data.tableId)) {
      this.scene.start(SceneKey.TableSelect);
      return;
    }

    // 伺服器模式：只能選這一桌的底台，且金幣要夠付桌費與入座門檻
    const economy = TABLE_ECONOMY[this.currentTableId()];
    const affordable = BetPlans.map(
      (bet, index) =>
        canAllPlayersAfford(this.save.ledger.balances, bet) &&
        (!this.server ||
          (economy.planIndexes.includes(index) &&
            this.save.ledger.balances[0] >= economy.threshold &&
            this.save.ledger.balances[0] - tableFeeFor(this.currentTableId(), bet) >=
              bet.base + bet.taiUnit))
    );
    if (!affordable[this.selectedIndex]) {
      this.selectedIndex = affordable.findIndex(Boolean);
    }

    this.renderPanel();
    this.renderBalances();
    this.renderBackButton();
    this.renderPlans(affordable);
    this.renderStartButton(affordable);
    this.bindKeyboard(affordable);
  }

  private previewSave(): IGameSave {
    const insufficient =
      new URLSearchParams(window.location.search).get('fixture') === 'insufficient';
    const balances = insufficient
      ? ([20, 20, 20, 20] as const)
      : ([1_000_000, 1_000_000, 1_000_000, 1_000_000] as const);
    return {
      schemaVersion: 1,
      ledger: {
        playerIds: ['player-self', 'npc-right', 'npc-opposite', 'npc-left'],
        balances,
        appliedTransactionIds: [],
      },
      activeSession: null,
    };
  }

  private renderPanel(): void {
    createPagePanel(this, PANEL_X, PANEL_Y, PANEL_WIDTH, PANEL_HEIGHT);
    createPageTitle(this, PANEL_X, TITLE_Y, '選擇底／台');
  }

  /** 新的一將用選桌畫面傳來的牌桌；換圈沿用原本的牌桌。 */
  private currentTableId(): TTableId {
    return findTable(this.save.activeSession?.tableAI?.tableId)?.id ?? this.tableId;
  }

  private renderBalances(): void {
    const account = accountSnapshot();
    const text = this.server
      ? `你的金幣：${this.save.ledger.balances[0].toLocaleString()}` +
        (account && account.freeSetsLeft !== null && !this.save.activeSession
          ? `，今天還可以開 ${account.freeSetsLeft} 將`
          : '')
      : `四家籌碼：${this.save.ledger.balances.map((value) => value.toLocaleString()).join('／')}`;
    this.add
      .text(PANEL_X, BALANCE_Y, text, {
        color: PAGE_THEME.text,
        fontFamily: PAGE_THEME.font,
        fontSize: `${BALANCE_FONT_SIZE}px`,
      })
      .setOrigin(0.5);
  }

  private renderPlans(affordable: readonly boolean[]): void {
    BetPlans.forEach((bet, index) => {
      const enabled = affordable[index] ?? false;
      const selected = index === this.selectedIndex;
      const y = PLAN_START_Y + index * PLAN_STEP_Y;
      const background = createDialogButtonSurface(
        this,
        PANEL_X,
        y,
        PLAN_WIDTH,
        PLAN_HEIGHT,
        selected && enabled
      );
      background.setAlpha(enabled ? 1 : DISABLED_ALPHA);
      const inTable =
        !this.server || TABLE_ECONOMY[this.currentTableId()].planIndexes.includes(index);
      const fee = this.server ? tableFeeFor(this.currentTableId(), bet) : 0;
      const suffix = !inTable
        ? '（其他牌桌）'
        : !enabled
          ? '（金幣不足）'
          : fee > 0
            ? `（桌費 ${fee.toLocaleString()}）`
            : '';
      const label = this.add
        .text(PANEL_X, y, `${bet.base} 底／${bet.taiUnit} 台${suffix}`, {
          color: enabled ? '#ffffff' : '#999999',
          fontFamily: PAGE_THEME.font,
          fontSize: `${PLAN_FONT_SIZE}px`,
          fontStyle: selected && enabled ? 'bold' : 'normal',
        })
        .setOrigin(0.5);
      this.planSurfaces.push(background);
      this.planLabels.push(label);
      if (enabled) {
        const hit = this.add
          .zone(PANEL_X, y, PLAN_WIDTH, PLAN_HEIGHT)
          .setInteractive({ useHandCursor: true })
          .on('pointerdown', () => {
            if (this.starting) return;
            playUIClick(this);
            this.selectPlan(index);
          });
        background.bindInteraction(hit);
      }
    });
    if (this.selectedIndex >= 0) {
      this.selectionFrame = this.add.graphics({
        x: PANEL_X,
        y: PLAN_START_Y + this.selectedIndex * PLAN_STEP_Y,
      });
      this.selectionFrame.lineStyle(SELECTION_LINE, PAGE_THEME.gold);
      this.selectionFrame.strokeRoundedRect(
        -PLAN_WIDTH / 2 - SELECTION_PAD,
        -PLAN_HEIGHT / 2 - SELECTION_PAD,
        PLAN_WIDTH + SELECTION_PAD * 2,
        PLAN_HEIGHT + SELECTION_PAD * 2,
        SELECTION_RADIUS
      );
    }
  }

  private selectPlan(index: number): void {
    if (index === this.selectedIndex || this.starting) return;
    this.selectedIndex = index;
    this.planSurfaces.forEach((surface, i) => surface.setSelected(i === index));
    this.planLabels.forEach((label, i) => label.setFontStyle(i === index ? 'bold' : 'normal'));
    const y = PLAN_START_Y + index * PLAN_STEP_Y;
    if (this.selectionFrame) animateTo(this, this.selectionFrame, { y }, Motion.selectMs);
    glow(this, PANEL_X, y, PLAN_WIDTH, PLAN_HEIGHT);
  }

  private renderStartButton(affordable: readonly boolean[]): void {
    const enabled = this.selectedIndex >= 0 && affordable[this.selectedIndex] === true;
    this.add
      .text(
        PANEL_X,
        INFO_Y,
        this.server
          ? '進階桌與高手桌開局時收桌費，新手桌免費'
          : '四家皆須至少持有「底＋一台」才能開始',
        {
          color: PAGE_THEME.muted,
          fontFamily: PAGE_THEME.font,
          fontSize: `${INFO_FONT_SIZE}px`,
        }
      )
      .setOrigin(0.5);
    const button = createPageButton(
      this,
      PANEL_X,
      START_Y,
      START_WIDTH,
      START_HEIGHT,
      enabled ? '開始牌局' : '籌碼不足',
      enabled
    );

    button.setPrimary();
    if (!enabled || this.preview) return;
    const hit = this.add
      .zone(PANEL_X, START_Y, START_WIDTH, START_HEIGHT)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        playUIClick(this);
        this.startWithBet(BetPlans[this.selectedIndex]!);
      });
    button.bindInteraction(hit);
  }

  private startWithBet(bet: IBetPlan): void {
    if (this.starting || this.preview) return;
    this.starting = true;
    if (this.server) {
      this.startOnServer(BetPlans.indexOf(bet as (typeof BetPlans)[number]));
      return;
    }
    try {
      const id = (prefix: string): string =>
        `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`;
      const wallSeed = Math.floor(Math.random() * 0x1_0000_0000);
      const active = this.save.activeSession;
      const session =
        active?.status === GameSetStatus.CircleComplete
          ? prepareNextRound(active, {
              roundId: id('round'),
              transactionId: id('settlement'),
              openingDice: rollDice(Math.random),
              wallSeed,
              bet,
            })
          : createGameSet({
              tableAI: createSelectedTableProfile(this.tableId),
              sessionId: id('session'),
              roundId: id('round'),
              transactionId: id('settlement'),
              balances: this.save.ledger.balances,
              bet,
              dealerDice: rollDice(Math.random),
              openingDice: rollDice(Math.random),
              wallSeed,
            });
      saveActiveSession(session);
      traceGameSet(
        active?.status === GameSetStatus.CircleComplete ? 'round.plan.saved' : 'session.created',
        session,
        {},
        this.save.ledger.balances
      );
      if (!active) {
        traceGameSet('round.plan.saved', session, {}, this.save.ledger.balances);
      }
      leaveScene(this, () => this.scene.start(SceneKey.Dice, { autoStartGame: true }));
    } catch (error) {
      this.starting = false;
      this.renderFatalError(error);
    }
  }

  /** 伺服器開將（新的一將）或換圈；成功後伺服器快照已寫回本機存檔，直接進擲骰。 */
  private startOnServer(planIndex: number): void {
    const circle = this.save.activeSession?.status === GameSetStatus.CircleComplete;
    const request = circle ? nextRound(planIndex) : startSet(this.tableId, planIndex);
    request
      .then(() => leaveScene(this, () => this.scene.start(SceneKey.Dice, { autoStartGame: true })))
      .catch((error: unknown) => {
        this.starting = false;
        const message = error instanceof Error ? error.message : '';
        const prompted =
          message.includes('免費將數') &&
          maybeShowHelpPrompt('今天的免費將數用完了', () => this.scene.start(SceneKey.Shop));
        if (!prompted) noticeFromError(error);
      });
  }

  private bindKeyboard(affordable: readonly boolean[]): void {
    const keyboard = this.input.keyboard;
    if (!keyboard) return;
    const handleKeyboard = (event: KeyboardEvent): void => {
      if (this.starting) return;
      if (event.key === 'Escape' || event.key === 'Backspace') {
        playUIClick(this);
        this.goBack();
        event.preventDefault();
        return;
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        const direction = event.key === 'ArrowUp' ? -1 : 1;
        let next = this.selectedIndex;
        for (let count = 0; count < affordable.length; count++) {
          next = (next + direction + affordable.length) % affordable.length;
          if (affordable[next]) break;
        }
        if (affordable[next]) {
          playUIClick(this);
          this.selectPlan(next);
        }
        event.preventDefault();
        return;
      }
      if (
        (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') &&
        !this.preview &&
        this.selectedIndex >= 0 &&
        affordable[this.selectedIndex]
      ) {
        playUIClick(this);
        this.startWithBet(BetPlans[this.selectedIndex]!);
        event.preventDefault();
      }
    };
    keyboard.on('keydown', handleKeyboard);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => keyboard.off('keydown', handleKeyboard));
  }

  private goBack(): void {
    if (this.starting) return;
    if (this.save.activeSession) this.scene.start(SceneKey.Home);
    else this.scene.start(SceneKey.TableSelect, { tableId: this.tableId });
  }

  private renderBackButton(): void {
    const active = this.save.activeSession;
    const name = active
      ? (findTable(active.tableAI?.tableId)?.name ?? '原牌桌')
      : findTable(this.tableId)!.name;
    this.add
      .text(PANEL_X, TABLE_LABEL_Y, `本圈牌桌：${name}`, {
        fontFamily: PAGE_THEME.font,
        fontSize: `${INFO_FONT_SIZE}px`,
        color: PAGE_THEME.muted,
      })
      .setOrigin(0.5);
    createCloseButton(
      this,
      PANEL_X + PANEL_WIDTH / 2 - CloseButtonLayout.insetX,
      PANEL_Y - PANEL_HEIGHT / 2 + CloseButtonLayout.insetY,
      () => {
        playUIClick(this);
        this.goBack();
      }
    );
  }

  private renderFatalError(error: unknown): void {
    this.add
      .text(
        LOGICAL_WIDTH / 2,
        LOGICAL_HEIGHT / 2,
        `永久籌碼資料無法讀取\n${error instanceof Error ? error.message : String(error)}`,
        {
          align: 'center',
          color: PAGE_THEME.text,
          fontFamily: PAGE_THEME.font,
          fontSize: '28px',
          wordWrap: { width: 900 },
        }
      )
      .setOrigin(0.5);
  }
}
